import React from "react";
import { Edit3, Save, MapPin, FileText, Printer } from "lucide-react";
import { fmtDate } from "../../lib/format";
import { openCustomerFile, openReceivingPdf } from "../DispatchCloseoutModal";
import { ContractReviewBadge } from "./ContractReviewBadge";

/**
 * Job-level action buttons (Receiving PDF, Customer File, Close-out, Edit /
 * Save / Cancel) — rendered in the JobDetailModal's top title bar
 * (via Modal's `headerActions` slot) so they're visible the instant the job
 * opens, no scrolling past the header card needed.
 */
export function JobHeaderActions({
  editing, setEditing, saveHeader, cancelEdit, busy,
  canEdit, canDispatch, isTechnician, onOpenCloseout, jobId,
}) {
  return (
    <>
      <button className="btn ghost sm" onClick={() => openReceivingPdf(jobId)} data-testid="job-receiving-pdf">
        <Printer size={13} /> Receiving PDF
      </button>
      {!isTechnician && (
        <button className="btn ghost sm" onClick={() => openCustomerFile(jobId)} data-testid="job-customer-file">
          <FileText size={13} /> Customer File
        </button>
      )}
      {canDispatch && (
        <button className="btn ghost sm" onClick={onOpenCloseout} data-testid="job-closeout-open">
          <Save size={13} /> Close-out
        </button>
      )}
      {canEdit && !editing && (
        <button className="btn ghost sm" onClick={() => setEditing(true)} data-testid="job-header-edit">
          <Edit3 size={13} /> Edit
        </button>
      )}
      {canEdit && editing && (
        <>
          <button className="btn ghost sm" onClick={cancelEdit} disabled={busy}>Cancel</button>
          <button className="btn gold sm" onClick={saveHeader} disabled={busy} data-testid="job-header-save">
            <Save size={13} /> {busy ? "Saving…" : "Save"}
          </button>
        </>
      )}
    </>
  );
}

/**
 * Job header card — read & edit view of the job-level metadata
 * (customer, DN, dates, contact, end-user, admin notes, certificate address,
 * contract-review banner).
 *
 * Stateless beyond what the parent passes in: edit state, header draft and
 * busy flag live in the parent JobDetailModal so save/cancel can re-fetch
 * the job afterwards.
 */
export function JobHeaderCard({
  job, hdr, setHdr,
  editing, setEditing,
  saveHeader, cancelEdit,
  busy,
  canEdit, canDispatch, isTechnician, isManager,
  onOpenCloseout, onChangeCustomer,
  jobId,
}) {
  return (
    <div className="card pad" style={{ background: "var(--bg-soft, #fafafa)", marginBottom: 14 }}>
      <div className="form-grid">
        <div className="fld"><label>Customer</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className="t-strong">{job.customer_number && <span className="mono" style={{ color: "var(--gold-deep)", marginRight: 8 }}>{job.customer_number}</span>}{job.customer_name || "—"}</div>
            {isManager && (
              <button className="btn ghost sm" onClick={onChangeCustomer} data-testid="job-hdr-change-customer">Change</button>
            )}
          </div>
        </div>
        <div className="fld"><label>Delivery note</label>
          {editing && !isTechnician
            ? <input className="input mono t-strong" value={hdr.delivery_note_number} onChange={(e) => setHdr({ ...hdr, delivery_note_number: e.target.value })} data-testid="job-hdr-dn" />
            : <div className="mono t-strong" data-testid="job-hdr-dn-readonly">{job.delivery_note_number || "—"}</div>}</div>
        <div className="fld"><label>Date received</label>
          {editing && isManager
            ? <input className="input mono" type="date" value={hdr.date_received || ""} onChange={(e) => setHdr({ ...hdr, date_received: e.target.value })} data-testid="job-hdr-date-received" />
            : <div className="mono" data-testid="job-hdr-date-received-readonly">{fmtDate(job.date_received)}</div>}</div>
        <div className="fld"><label>Received by</label><div className="t-mut">{job.received_by_name || "—"}</div></div>
        <div className="fld"><label>Calibration type</label><div className="t-mut">{job.cal_type === "sanas" ? "SANAS Accredited" : "Traceable"}</div></div>
        <div className="fld"><label>Sage quote(s) / ref</label>
          {editing && !isTechnician
            ? <><input className="input mono" value={hdr.quote_number} onChange={(e) => setHdr({ ...hdr, quote_number: e.target.value })} data-testid="job-hdr-quote" />
                <div className="t-mut" style={{ fontSize: 11, marginTop: 3 }} data-testid="job-hdr-quote-hint">Reference only. Use "Add quote" above the lines to link a quote to items.</div></>
                   : <div className="mono" data-testid="job-hdr-quote-readonly">{(() => {
                       // SPT-PTE-2026-0071 — list every quote on the DN's lines.
                       const qs = [...new Set((job.items || []).map((i) => i.quote_number).filter(Boolean))];
                       if (job.quote_number && !qs.includes(job.quote_number)) qs.unshift(job.quote_number);
                       return qs.length ? qs.join(", ") : "—";
                     })()}</div>}</div>
        <div className="fld"><label>Certificate number(s)</label>
          <div className="mono t-strong" data-testid="job-hdr-cert-numbers">{job.internal_job_reference || (job.certificate_numbers || []).join(", ") || "—"}</div></div>
        <div className="fld"><label>Contact person</label>
          {editing ? <input className="input" value={hdr.contact_person} onChange={(e) => setHdr({ ...hdr, contact_person: e.target.value })} data-testid="job-hdr-contact" />
                   : <div>{job.contact_person || "—"}</div>}</div>
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>End user (job-level contact)</label>
          {editing ? (
            <>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, marginBottom: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!hdr.end_user} onChange={(e) => setHdr({ ...hdr, end_user: e.target.checked })} data-testid="job-hdr-enduser-toggle" />
                Certificate goes to a different end user (not the billing customer)
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <input className="input" placeholder="Contact name" value={hdr.end_user_name} onChange={(e) => setHdr({ ...hdr, end_user_name: e.target.value })} />
                <input className="input mono" placeholder="Email" value={hdr.end_user_email} onChange={(e) => setHdr({ ...hdr, end_user_email: e.target.value })} />
                <input className="input mono" placeholder="Phone" value={hdr.end_user_phone} onChange={(e) => setHdr({ ...hdr, end_user_phone: e.target.value })} />
              </div>
              {hdr.end_user && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--line)" }}>
                  <div className="t-mut" style={{ fontSize: 11.5, marginBottom: 8 }}><MapPin size={12} style={{ verticalAlign: "-2px" }} /> End-user certificate name &amp; address (billing customer unchanged)</div>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!hdr.end_user_address_unknown} onChange={(e) => setHdr({ ...hdr, end_user_address_unknown: e.target.checked })} data-testid="job-hdr-enduser-addr-unknown" />
                    Address not supplied (record a reason)
                  </label>
                  {hdr.end_user_address_unknown ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8 }}>
                      <input className="input" placeholder="Company / name" value={hdr.end_user_company} onChange={(e) => setHdr({ ...hdr, end_user_company: e.target.value })} data-testid="job-hdr-enduser-company" />
                      <input className="input" placeholder="Reason address not supplied" value={hdr.end_user_address_unknown_reason} onChange={(e) => setHdr({ ...hdr, end_user_address_unknown_reason: e.target.value })} data-testid="job-hdr-enduser-addr-reason" />
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      <input className="input" placeholder="Company / name" value={hdr.end_user_company} onChange={(e) => setHdr({ ...hdr, end_user_company: e.target.value })} data-testid="job-hdr-enduser-company" />
                      <input className="input" placeholder="Address line 1" value={hdr.end_user_address_line_1} onChange={(e) => setHdr({ ...hdr, end_user_address_line_1: e.target.value })} data-testid="job-hdr-enduser-addr1" />
                      <input className="input" placeholder="Address line 2" value={hdr.end_user_address_line_2} onChange={(e) => setHdr({ ...hdr, end_user_address_line_2: e.target.value })} />
                      <input className="input" placeholder="City" value={hdr.end_user_city} onChange={(e) => setHdr({ ...hdr, end_user_city: e.target.value })} />
                      <input className="input" placeholder="Province" value={hdr.end_user_province} onChange={(e) => setHdr({ ...hdr, end_user_province: e.target.value })} />
                      <input className="input" placeholder="Country" value={hdr.end_user_country} onChange={(e) => setHdr({ ...hdr, end_user_country: e.target.value })} />
                      <input className="input mono" placeholder="Postal code" value={hdr.end_user_postal_code} onChange={(e) => setHdr({ ...hdr, end_user_postal_code: e.target.value })} />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="t-mut">{job.end_user_name || "—"}{job.end_user_email && <span className="mono"> · {job.end_user_email}</span>}{job.end_user_phone && <span className="mono"> · {job.end_user_phone}</span>}</div>
          )}
        </div>
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Admin notes (staff only)</label>
          {editing ? <textarea className="input" rows={2} value={hdr.notes_admin} onChange={(e) => setHdr({ ...hdr, notes_admin: e.target.value })} data-testid="job-hdr-notes" />
                   : <div className="t-mut" style={{ whiteSpace: "pre-wrap" }}>{job.notes_admin || "—"}</div>}
        </div>
      </div>
      {job.certificate_address && (() => {
        const ca = job.certificate_address;
        const lines = ca.address_unknown
          ? [`Address not supplied${ca.address_unknown_reason ? ` — ${ca.address_unknown_reason}` : ""}`]
          : [ca.address_line_1, ca.address_line_2,
             [ca.city, ca.province].filter(Boolean).join(", "),
             [ca.country, ca.postal_code].filter(Boolean).join(" · ")].filter(Boolean);
        return (
          <div className="card pad" data-testid="job-cert-address"
            style={{ marginTop: 12, background: "var(--card)", borderLeft: `3px solid ${ca.source === "end_user" ? "var(--gold-deep)" : "var(--line)"}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <MapPin size={14} color="var(--gold-deep)" />
              <span className="t-strong" style={{ fontSize: 12.5 }}>Certificate address</span>
              <span className={`badge ${ca.source === "end_user" ? "b-gold" : "b-slate"}`} style={{ fontSize: 10 }} data-testid="job-cert-address-source">
                <span className="bd" />{ca.source === "end_user" ? "End user" : "Customer"}
              </span>
            </div>
            <div className="t-strong" style={{ fontSize: 13 }}>{ca.name || "—"}</div>
            <div className="t-mut" style={{ fontSize: 12, marginTop: 2, whiteSpace: "pre-line" }}>{lines.length ? lines.join("\n") : "No address on record"}</div>
          </div>
        );
      })()}
      {job.contract_review_submission_id && (
        <ContractReviewBadge subId={job.contract_review_submission_id}
                             signedBy={job.contract_review_signed_by}
                             signedAt={job.contract_review_signed_at} />
      )}
    </div>
  );
}
